import React from "react";
import { Scale, MapPin, Calculator, FileCheck, Building2, Briefcase, Receipt, TrendingUp } from "lucide-react";

/**
 * BrandedHero — programmatic article hero for the 16 new launch articles.
 *
 * Eight variants cycle through the Instabridge palette so a long index page
 * has visual rhythm without any external stock photography.  The component
 * renders inline (no <img>) which means the hero loads instantly and ships
 * zero KB of image weight to the user.
 */
const VARIANTS = [
  { from: "#0A2540", to: "#001A3D", icon: Scale,        accent: "#FAD28C" }, // 1 legal/regulation
  { from: "#0066FF", to: "#001A3D", icon: MapPin,       accent: "#A7F3D0" }, // 2 ohio / geography
  { from: "#001A3D", to: "#0A2540", icon: Calculator,   accent: "#FAD28C" }, // 3 rates / pricing
  { from: "#0A2540", to: "#0066FF", icon: FileCheck,    accent: "#A7F3D0" }, // 4 eligibility
  { from: "#0A2540", to: "#1E40AF", icon: Building2,    accent: "#FAD28C" }, // 5 companies / comparison
  { from: "#0066FF", to: "#0A2540", icon: Briefcase,    accent: "#A7F3D0" }, // 6 attorney
  { from: "#001A3D", to: "#0066FF", icon: Receipt,      accent: "#FAD28C" }, // 7 tax
  { from: "#0A2540", to: "#001A3D", icon: TrendingUp,   accent: "#A7F3D0" }, // 8 negotiation / leverage
];

export default function BrandedHero({ variant = 1, eyebrow, title }) {
  const n = Number(variant);
  const idx = Number.isFinite(n) && n > 0
    ? (Math.floor(n) - 1) % VARIANTS.length
    : 0;
  const v = VARIANTS[idx];
  const Icon = v.icon;
  return (
    <div
      className="aspect-[16/9] w-full rounded-2xl overflow-hidden relative isolate"
      style={{ background: `linear-gradient(135deg, ${v.from} 0%, ${v.to} 100%)` }}
      data-testid={`branded-hero-${variant}`}
    >
      {/* Soft grid pattern overlay */}
      <svg className="absolute inset-0 w-full h-full opacity-10" aria-hidden="true">
        <defs>
          <pattern id={`grid-${variant}`} width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#grid-${variant})`} />
      </svg>
      {/* Accent corner glow */}
      <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full opacity-30 blur-3xl" style={{ background: v.accent }} />
      <div className="absolute -bottom-16 -left-16 w-80 h-80 rounded-full opacity-15 blur-3xl bg-white" />
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-between p-7 sm:p-10 lg:p-12">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: v.accent }}>
          <span className="inline-block w-6 h-px" style={{ background: v.accent }} />
          {eyebrow || "Instabridge Insight"}
        </div>
        <div className="flex items-end justify-between gap-6">
          <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight max-w-[80%]">
            {title}
          </h2>
          <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/15">
            <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </div>
  );
}
